import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  TablePagination,
  LinearProgress,
  Checkbox,
  Link
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Payment as PaymentIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import {
  getPayments,
  recordPayment,
  getAgingReport,
  getInvoices,
  getSuppliers,
  formatCurrency,
  formatDate,
  getStatusColor,
  getPaymentMethodLabel
} from '../../services/procurementService';
import { useBrand } from '../../context/BrandContext';
import SupplierPicker from '../common/SupplierPicker';

function Payments() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { selectedBrand } = useBrand();
  const invoiceIdFromUrl = searchParams.get('invoice_id');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Data state
  const [payments, setPayments] = useState([]);
  const [agingData, setAgingData] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [pendingInvoices, setPendingInvoices] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [supplierFilter, setSupplierFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(invoiceIdFromUrl ? true : false);
  const [paymentForm, setPaymentForm] = useState({
    supplier_id: '',
    payment_date: new Date().toISOString().split('T')[0],
    payment_amount: '',
    payment_method: 'bank_transfer',
    payment_reference: '',
    notes: '',
    invoice_allocations: []
  });
  const [selectedSupplierInvoices, setSelectedSupplierInvoices] = useState([]);

  useEffect(() => {
    loadData();
  }, [selectedBrand?.id]);

  useEffect(() => {
    if (invoiceIdFromUrl) {
      loadInvoiceForPayment(invoiceIdFromUrl);
    }
  }, [invoiceIdFromUrl]);

  const loadData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Note: Procurement operates at company level, not brand level
      const [paymentsRes, agingRes, suppliersRes] = await Promise.all([
        getPayments({ limit: 100 }),
        getAgingReport(),
        getSuppliers({ is_active: true, limit: 100 })
      ]);

      // Handle response structures
      const paymentsData = paymentsRes?.data;
      setPayments(Array.isArray(paymentsData) ? paymentsData : (paymentsData?.payments || []));
      setAgingData(agingRes?.data || {});
      const suppliersData = suppliersRes?.data;
      setSuppliers(Array.isArray(suppliersData) ? suppliersData : []);
    } catch (err) {
      console.error('Failed to load data:', err);
      setError('Failed to load payments data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadInvoiceForPayment = async (invoiceId) => {
    try {
      const res = await getInvoices({ id: invoiceId });
      const invoice = res?.data?.[0];
      if (invoice) {
        setPaymentForm({
          ...paymentForm,
          supplier_id: invoice.supplier_id,
          payment_amount: invoice.total_amount - (invoice.paid_amount || 0),
          invoice_allocations: [{
            invoice_id: invoice.id,
            invoice_number: invoice.invoice_number,
            total_amount: invoice.total_amount,
            balance: invoice.total_amount - (invoice.paid_amount || 0),
            allocated_amount: invoice.total_amount - (invoice.paid_amount || 0)
          }]
        });
        await loadSupplierInvoices(invoice.supplier_id);
      }
    } catch (err) {
      console.error('Failed to load invoice:', err);
    }
  };

  const loadSupplierInvoices = async (supplierId) => {
    try {
      const res = await getInvoices({ supplier_id: supplierId, status: 'approved' });
      // Handle response structure
      const invoicesData = res?.data;
      setSelectedSupplierInvoices(Array.isArray(invoicesData) ? invoicesData : (invoicesData?.invoices || []));
    } catch (err) {
      console.error('Failed to load supplier invoices:', err);
    }
  };

  const handleSupplierChange = async (supplierId) => {
    setPaymentForm({
      ...paymentForm,
      supplier_id: supplierId,
      invoice_allocations: []
    });
    if (supplierId) {
      await loadSupplierInvoices(supplierId);
    } else {
      setSelectedSupplierInvoices([]);
    }
  };

  const handleInvoiceToggle = (invoice) => {
    const existing = paymentForm.invoice_allocations.find(a => a.invoice_id === invoice.id);
    let newAllocations;

    if (existing) {
      newAllocations = paymentForm.invoice_allocations.filter(a => a.invoice_id !== invoice.id);
    } else {
      const balance = invoice.total_amount - (invoice.paid_amount || 0);
      newAllocations = [
        ...paymentForm.invoice_allocations,
        {
          invoice_id: invoice.id,
          invoice_number: invoice.invoice_number,
          total_amount: invoice.total_amount,
          balance: balance,
          allocated_amount: balance
        }
      ];
    }

    const totalAllocation = newAllocations.reduce((sum, a) => sum + (parseFloat(a.allocated_amount) || 0), 0);
    setPaymentForm({
      ...paymentForm,
      invoice_allocations: newAllocations,
      payment_amount: totalAllocation
    });
  };

  const handleAllocationChange = (invoiceId, amount) => {
    const newAllocations = paymentForm.invoice_allocations.map(a => {
      if (a.invoice_id === invoiceId) {
        return { ...a, allocated_amount: parseFloat(amount) || 0 };
      }
      return a;
    });

    const totalAllocation = newAllocations.reduce((sum, a) => sum + (parseFloat(a.allocated_amount) || 0), 0);
    setPaymentForm({
      ...paymentForm,
      invoice_allocations: newAllocations,
      payment_amount: totalAllocation
    });
  };

  const handleSave = async () => {
    try {
      setError(null);
      await recordPayment(paymentForm);
      setSuccess('Payment recorded successfully');
      setDialogOpen(false);
      loadData();
      // Reset form
      setPaymentForm({
        supplier_id: '',
        payment_date: new Date().toISOString().split('T')[0],
        payment_amount: '',
        payment_method: 'bank_transfer',
        payment_reference: '',
        notes: '',
        invoice_allocations: []
      });
      setSelectedSupplierInvoices([]);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to record payment');
    }
  };

  const filteredPayments = payments.filter(payment => {
    const searchLower = searchTerm.toLowerCase();
    return (
      payment.payment_reference?.toLowerCase().includes(searchLower) ||
      payment.supplier_name?.toLowerCase().includes(searchLower)
    );
  });

  // Outstanding bills that make up the "Total Outstanding" figure. The aging
  // endpoint already returns them (our own supplier_invoices, PO-supplier scoped);
  // the page just never rendered the breakdown. Each row keeps its NATIVE currency
  // and amount — the USD "Total Outstanding" is a converted summary, so we never
  // sum across currencies here (per the never-mixed-currency rule).
  const outstandingBills = Array.isArray(agingData?.invoices) ? agingData.invoices : [];
  const outstandingByCurrency = outstandingBills.reduce((acc, b) => {
    const c = String(b.currency || agingData?.summary_currency || 'USD').toUpperCase();
    acc[c] = (acc[c] || 0) + (Number(b.outstanding_amount) || 0);
    return acc;
  }, {});
  const overdueBillCount = outstandingBills.filter(b => Number(b.days_overdue) > 0).length;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Supplier Payments
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Record payments and manage accounts payable
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => { setError(null); setDialogOpen(true); }}
          >
            Record Payment
          </Button>
          <Tooltip title="Refresh">
            <IconButton onClick={loadData}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Aging Summary */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight="600" gutterBottom>Accounts Payable Aging</Typography>
              {agingData && (
                <Box>
                  {[
                    { label: 'Current', value: agingData.summary?.current?.amount || 0, color: 'success' },
                    { label: '1-30 days', value: agingData.summary?.['1-30']?.amount || 0, color: 'success' },
                    { label: '31-60 days', value: agingData.summary?.['31-60']?.amount || 0, color: 'info' },
                    { label: '61-90 days', value: agingData.summary?.['61-90']?.amount || 0, color: 'warning' },
                    { label: 'Over 90 days', value: agingData.summary?.['90+']?.amount || 0, color: 'error' }
                  ].map((bucket) => (
                    <Box key={bucket.label} sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2">{bucket.label}</Typography>
                        <Typography variant="body2" fontWeight="600">
                          {formatCurrency(bucket.value, agingData.summary_currency || 'USD')}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={agingData.total_outstanding > 0
                          ? (bucket.value / agingData.total_outstanding) * 100
                          : 0
                        }
                        color={bucket.color}
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                    </Box>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" fontWeight="600" gutterBottom>Summary</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="body2" color="textSecondary">Total Outstanding</Typography>
                  <Typography variant="h4" fontWeight="bold" color="primary">
                    {formatCurrency(agingData?.total_outstanding || 0, agingData?.summary_currency || 'USD')}
                  </Typography>
                </Box>
                {agingData?.overdue_count > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
                    <WarningIcon />
                    <Typography fontWeight="medium">
                      {agingData.overdue_count} overdue invoices
                    </Typography>
                  </Box>
                )}
                <Box>
                  <Typography variant="body2" color="textSecondary">Paid This Month</Typography>
                  <Typography variant="h5" fontWeight="bold" color="success.main">
                    {formatCurrency(agingData?.paid_this_month || 0)}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Outstanding Bills — what actually makes up the Total Outstanding */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 1, mb: 1 }}>
            <Typography variant="h6" fontWeight="600">
              Outstanding Bills ({outstandingBills.length})
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {Object.entries(outstandingByCurrency).map(([ccy, amt]) => (
                <Chip
                  key={ccy}
                  label={formatCurrency(amt, ccy)}
                  size="small"
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Open supplier invoices from our own records (suppliers we raise POs with).
            {overdueBillCount > 0 && ` ${overdueBillCount} overdue.`} Amounts are shown in each
            bill's own currency; the “Total Outstanding” above is the{' '}
            {agingData?.summary_currency || 'USD'} equivalent.
          </Typography>
          <TableContainer sx={{ maxHeight: 440 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Supplier</TableCell>
                  <TableCell>Invoice #</TableCell>
                  <TableCell>Due Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Outstanding</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {outstandingBills.map((bill, idx) => {
                  const daysOverdue = Number(bill.days_overdue) || 0;
                  return (
                    <TableRow key={`${bill.id}-${bill.schedule_id || 0}-${idx}`} hover>
                      <TableCell>
                        {bill.supplier_id ? (
                          <Link
                            component="button"
                            type="button"
                            underline="hover"
                            sx={{ textAlign: 'left', fontWeight: 'medium' }}
                            onClick={() => navigate(`/procurement/suppliers/${bill.supplier_id}?tab=payments`)}
                            title="View this supplier's bills and payment activity"
                          >
                            {bill.supplier_name || '—'}
                          </Link>
                        ) : (
                          bill.supplier_name || '—'
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {bill.invoice_number || '—'}
                        </Typography>
                        {bill.milestone_name && bill.payable_source === 'schedule' && (
                          <Typography variant="caption" color="textSecondary">
                            {bill.milestone_name}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{formatDate(bill.due_date)}</TableCell>
                      <TableCell>
                        {daysOverdue > 0 ? (
                          <Chip
                            label={`${daysOverdue}d overdue`}
                            size="small"
                            color={daysOverdue > 60 ? 'error' : 'warning'}
                          />
                        ) : (
                          <Chip
                            label={daysOverdue === 0 ? 'due today' : `due in ${-daysOverdue}d`}
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Typography fontWeight="medium">
                          {formatCurrency(bill.outstanding_amount, bill.currency)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {outstandingBills.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Box sx={{ py: 3 }}>
                        <Typography color="textSecondary">Nothing outstanding.</Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={5}>
              <TextField
                placeholder="Search payments..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  )
                }}
                size="small"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <SupplierPicker
                value={supplierFilter}
                onChange={(sid) => setSupplierFilter(sid)}
                suppliers={suppliers}
                includeAllOption
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Button variant="outlined" onClick={loadData} fullWidth>
                Apply Filters
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Payment History — payments already MADE (distinct from Outstanding Bills above) */}
      <Card>
        <CardContent>
          <Typography variant="h6" fontWeight="600" gutterBottom>Payment History</Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Payments already made. Some imported bank/QuickBooks payments carry no reference of
            their own — those show “—”.
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Reference</TableCell>
                  <TableCell>Supplier</TableCell>
                  <TableCell>Payment Date</TableCell>
                  <TableCell>Method</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Invoices</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredPayments
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((payment) => (
                    <TableRow key={payment.id} hover>
                      <TableCell>
                        <Typography fontWeight="medium" color={payment.payment_reference ? 'text.primary' : 'text.disabled'}>
                          {payment.payment_reference || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>{payment.supplier_name}</TableCell>
                      <TableCell>{formatDate(payment.payment_date)}</TableCell>
                      <TableCell>
                        <Chip
                          label={getPaymentMethodLabel(payment.payment_method)}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography fontWeight="medium" color="success.main">
                          {formatCurrency(payment.payment_amount, payment.currency)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {payment.invoice_count || 0} invoice(s)
                      </TableCell>
                    </TableRow>
                  ))}
                {filteredPayments.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Box sx={{ py: 4 }}>
                        <PaymentIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                        <Typography color="textSecondary">
                          No payments recorded
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filteredPayments.length}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </CardContent>
      </Card>

      {/* Record Payment Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Record Supplier Payment</DialogTitle>
        <DialogContent>
          {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <SupplierPicker
                  value={paymentForm.supplier_id}
                  onChange={(sid) => handleSupplierChange(sid)}
                  suppliers={suppliers}
                  label="Supplier"
                  required
                  size="medium"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Payment Date"
                  type="date"
                  value={paymentForm.payment_date}
                  onChange={(e) => setPaymentForm({ ...paymentForm, payment_date: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  required
                  fullWidth
                />
              </Grid>
            </Grid>

            {paymentForm.supplier_id && selectedSupplierInvoices.length > 0 && (
              <>
                <Typography variant="subtitle2" color="textSecondary" sx={{ mt: 2 }}>
                  Select Invoices to Pay
                </Typography>
                <TableContainer sx={{ maxHeight: 300 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">Select</TableCell>
                        <TableCell>Invoice #</TableCell>
                        <TableCell>Due Date</TableCell>
                        <TableCell align="right">Total</TableCell>
                        <TableCell align="right">Balance</TableCell>
                        <TableCell align="right">Allocate</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedSupplierInvoices.map((invoice) => {
                        const allocation = paymentForm.invoice_allocations.find(a => a.invoice_id === invoice.id);
                        const isSelected = !!allocation;
                        const balance = invoice.total_amount - (invoice.paid_amount || 0);
                        return (
                          <TableRow key={invoice.id}>
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={isSelected}
                                onChange={() => handleInvoiceToggle(invoice)}
                              />
                            </TableCell>
                            <TableCell>{invoice.invoice_number}</TableCell>
                            <TableCell>{formatDate(invoice.due_date)}</TableCell>
                            <TableCell align="right">{formatCurrency(invoice.total_amount)}</TableCell>
                            <TableCell align="right">{formatCurrency(balance)}</TableCell>
                            <TableCell align="right">
                              {isSelected && (
                                <TextField
                                  type="number"
                                  value={allocation?.allocated_amount || ''}
                                  onChange={(e) => handleAllocationChange(invoice.id, e.target.value)}
                                  size="small"
                                  sx={{ width: 100 }}
                                  inputProps={{ max: balance, min: 0, step: 0.01 }}
                                />
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}

            {paymentForm.supplier_id && selectedSupplierInvoices.length === 0 && (
              <Alert severity="info">
                No approved invoices pending payment for this supplier.
              </Alert>
            )}

            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Payment Amount"
                  type="number"
                  value={paymentForm.payment_amount}
                  onChange={(e) => setPaymentForm({ ...paymentForm, payment_amount: e.target.value })}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">GBP</InputAdornment>
                  }}
                  required
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth required>
                  <InputLabel>Payment Method</InputLabel>
                  <Select
                    value={paymentForm.payment_method}
                    label="Payment Method"
                    onChange={(e) => setPaymentForm({ ...paymentForm, payment_method: e.target.value })}
                  >
                    <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                    <MenuItem value="check">Check</MenuItem>
                    <MenuItem value="cash">Cash</MenuItem>
                    <MenuItem value="credit_card">Credit Card</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Payment Reference"
                  value={paymentForm.payment_reference}
                  onChange={(e) => setPaymentForm({ ...paymentForm, payment_reference: e.target.value })}
                  required
                  fullWidth
                  placeholder="e.g., CHQ-001 or TRF-20240101"
                />
              </Grid>
            </Grid>

            <TextField
              label="Notes"
              value={paymentForm.notes}
              onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />

            <Box sx={{ mt: 2, p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
              <Typography variant="h6" color="success.contrastText">
                Total Payment: {formatCurrency(paymentForm.payment_amount || 0)}
              </Typography>
              {paymentForm.invoice_allocations.length > 0 && (
                <Typography variant="body2" color="success.contrastText">
                  Allocated to {paymentForm.invoice_allocations.length} invoice(s)
                </Typography>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={
              !paymentForm.supplier_id ||
              !paymentForm.payment_amount ||
              !paymentForm.payment_reference ||
              paymentForm.invoice_allocations.length === 0
            }
          >
            Record Payment
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Payments;
